import { useCallback, useMemo, useState } from "react";
import {
    OrganizationTaskTemplatesView,
    OrganizationTaskTemplatesViewColumn,
} from "@common/types/views/organizationTaskTemplatesView";
import { FiltersTypes } from "@common/types/filters/general";
import { ViewDensity, ViewType } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    FiltersChangeFn,
    FilterTabs,
} from "@components/pageHeader/filters/lib";
import { useActiveFilters } from "@components/pageHeader/filters/useActiveFilters";
import {
    ARCHIVE_FILTER_ITEMS,
    ArchiveFilter,
} from "../templates/archiveFilter";
import type { TaskTypeOption } from "../OrganizationCloseTemplates/closeTemplateTasksDrawer.constants";
import { AddOrganizationTaskTemplateDrawer } from "./AddOrganizationTaskTemplateDrawer";
import { OrganizationTaskTemplatesList } from "./OrganizationTaskTemplatesList";
import { organizationTaskTemplatesViewConfig } from "./organizationTaskTemplatesViewConfig";
import {
    DEFAULT_TEMPLATE_SORT,
    isDefaultTemplateSortColumn,
    OrganizationTaskTemplateSortValue,
} from "./organizationTaskTemplatesSort";
import { useOrganizationTaskTemplatesView } from "./useOrganizationTaskTemplatesView";

const AVAILABLE_FILTERS: FilterTabs[] = [
    FilterTabs.ORG_TASK_TEMPLATE_TYPE,
    FilterTabs.ORG_TASK_TEMPLATE_GROUP,
    FilterTabs.ORG_TASK_TEMPLATE_ASSIGNEE_ROLE,
    FilterTabs.ORG_TASK_TEMPLATE_CREATED_BY,
];

const defaultView: OrganizationTaskTemplatesView = {
    type: ViewType.ORGANIZATION_TASK_TEMPLATES,
    columns: [
        {
            id: OrganizationTaskTemplatesViewColumn.NAME,
            options: { pin: "left" },
        },
        { id: OrganizationTaskTemplatesViewColumn.TASK_TYPE },
        { id: OrganizationTaskTemplatesViewColumn.ASSIGNEE },
        { id: OrganizationTaskTemplatesViewColumn.CLOSE_STAGE },
        { id: OrganizationTaskTemplatesViewColumn.DEADLINE },
        { id: OrganizationTaskTemplatesViewColumn.ESTIMATE },
        { id: OrganizationTaskTemplatesViewColumn.CLIENT },
        { id: OrganizationTaskTemplatesViewColumn.SERVICE_TEMPLATES },
        { id: OrganizationTaskTemplatesViewColumn.CREATED_BY },
        { id: OrganizationTaskTemplatesViewColumn.CREATED },
        { id: OrganizationTaskTemplatesViewColumn.UPDATED_BY },
        { id: OrganizationTaskTemplatesViewColumn.UPDATED },
        {
            id: OrganizationTaskTemplatesViewColumn.ACTIONS,
            options: { pin: "right" },
        },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

export const OrganizationTaskTemplatesPage = () => {
    const [search, setSearch] = useState("");
    const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active");
    const [filters, setFilters] = useState<FiltersTypes>({});
    const [sort, setSort] = useState<OrganizationTaskTemplateSortValue>(
        DEFAULT_TEMPLATE_SORT,
    );
    const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
    const [overriddenTaskTypeOption, setOverriddenTaskTypeOption] = useState<
        TaskTypeOption | undefined
    >(undefined);

    const { view, handleViewChange } =
        useOrganizationTaskTemplatesView(defaultView);

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (payload) => setFilters((prev) => ({ ...prev, ...payload })),
        [],
    );

    const handleSortRemove = useCallback(
        () => setSort(DEFAULT_TEMPLATE_SORT),
        [],
    );

    const handleClearFilters = useCallback(() => {
        setSearch("");
        setArchiveFilter("active");
        setFilters({});
    }, []);

    const openAddDrawer = useCallback(() => {
        setOverriddenTaskTypeOption(undefined);
        setIsAddDrawerOpen(true);
    }, []);
    const openOverrideDrawer = useCallback((option: TaskTypeOption) => {
        setOverriddenTaskTypeOption(option);
        setIsAddDrawerOpen(true);
    }, []);
    const closeAddDrawer = useCallback(() => setIsAddDrawerOpen(false), []);

    const activeFilters = useActiveFilters(filters, AVAILABLE_FILTERS);
    const hasActiveFilters = useMemo(
        () =>
            search !== "" ||
            archiveFilter !== "active" ||
            activeFilters.length > 0,
        [search, archiveFilter, activeFilters],
    );

    return (
        <main>
            <TablePageHeader
                search={search}
                onSearchChange={setSearch}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={AVAILABLE_FILTERS}
                isFullPage
            >
                <HStack gap={8} className="ml-auto">
                    <Picker.Select
                        items={ARCHIVE_FILTER_ITEMS}
                        value={archiveFilter}
                        onValueChange={(value) =>
                            setArchiveFilter(value ?? "active")
                        }
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={organizationTaskTemplatesViewConfig}
                        defaultView={defaultView}
                        onViewChange={handleViewChange}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyOptions={{
                            description: "Create task template",
                            preventDefault: true,
                        }}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={openAddDrawer}
                        >
                            New template
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>

            <OrganizationTaskTemplatesList
                search={search}
                archiveFilter={archiveFilter}
                filters={filters}
                sort={sort}
                onSortChange={setSort}
                onSortRemove={
                    isDefaultTemplateSortColumn(sort)
                        ? undefined
                        : handleSortRemove
                }
                view={view}
                onViewChange={handleViewChange}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                onCreateTemplate={openAddDrawer}
                onOverrideKickDefault={openOverrideDrawer}
            />

            <AddOrganizationTaskTemplateDrawer
                open={isAddDrawerOpen}
                initialTaskTypeOption={overriddenTaskTypeOption}
                onClose={closeAddDrawer}
            />
        </main>
    );
};
